import { useMutation } from '@tanstack/react-query'
import { Button, Divider, Flex, Form, Result, Typography } from 'antd'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  apiErrorCode,
  coreStepIndex,
  CoreStep,
  getOrCreateJourney,
  JourneyEntry,
  JourneyScope,
  OnboardingErrorField,
  registerProperties,
  track
} from 'src/analytics'
import {
  authorizationSocialAppleStartClientCreate,
  authorizationSocialGoogleStartClientCreate,
  authorizationV2SignupStartCreateMutation,
  type AuthorizationSocialAppleStartClientCreateData,
  type AuthorizationSocialGoogleStartClientCreateData,
  type UserRegisterStart
} from 'src/api/main'
import { defaultLang } from 'src/locales'
import { paths } from 'src/router'
import { getSocialAuthRedirectOrigin, useInlineApiErrors } from 'src/utils'

import {
  AuthLink,
  AuthHeader,
  RegisterFormItems,
  SocialMethodButtons,
  type AuthMethodButton
} from '../components'
import {
  AppleBrandIcon,
  GoogleBrandIcon
} from '../components/SocialProviderIcons'
import { useStyles } from '../styles'
import { getAuthUrl } from '../utils/authResponseAdapters'

const { Text } = Typography

type StartFormValues = Pick<
  UserRegisterStart,
  'first_name' | 'last_name' | 'email' | 'phone'
> & { terms_and_conditions: boolean }

export const Register = () => {
  const [startForm] = Form.useForm<StartFormValues>()
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null)
  const termsAccepted = Form.useWatch('terms_and_conditions', startForm)

  const { showApiErrors, errorAlert } = useInlineApiErrors()
  const { t, i18n } = useTranslation()
  const { styles } = useStyles()

  /** Guards the once-per-mount report against StrictMode's double invocation. */
  const reportedEntry = useRef(false)

  // This screen is where the Core journey begins for a brand-new customer, so it
  // is where the journey id is minted and `entry` is fixed for everything that
  // follows — including the workspace wizard, which is the same journey.
  useEffect(() => {
    if (reportedEntry.current) return
    reportedEntry.current = true

    const journey = getOrCreateJourney()
    registerProperties({
      scope: JourneyScope.CORE,
      entry: JourneyEntry.SIGNUP
    })

    if (journey.created) {
      track('onboarding_started', { entry: JourneyEntry.SIGNUP })
    }

    track('onboarding_step_viewed', {
      step: CoreStep.EMAIL,
      step_index: coreStepIndex(CoreStep.EMAIL)
    })
  }, [])

  const resolvedLanguage = (
    i18n.resolvedLanguage ??
    i18n.language ??
    defaultLang.value
  ).split('-')[0]
  const apiLanguage = (
    ['en', 'ru', 'ro'].includes(resolvedLanguage)
      ? resolvedLanguage
      : defaultLang.value
  ) as UserRegisterStart['language']

  const { mutateAsync: startGoogleSignUp, isPending: isPendingGoogle } =
    useMutation({
      mutationFn: () => {
        const redirectOrigin = getSocialAuthRedirectOrigin()
        const currentOrigin = window.location.origin
        const payload: AuthorizationSocialGoogleStartClientCreateData['body'] =
          {
            redirect_uri: `${redirectOrigin}${paths.auth.googleClientCallback}`
          }
        if (currentOrigin !== redirectOrigin) {
          payload.return_origin = currentOrigin
        }
        return authorizationSocialGoogleStartClientCreate({
          body: payload
        }).then(({ data }) => data)
      },
      onSuccess: (response) => {
        const authUrl = getAuthUrl(response)
        if (!authUrl) throw new Error('Missing Google auth URL')
        window.location.href = authUrl
      },
      onError: (e) => showApiErrors(e, startForm)
    })

  const { mutateAsync: startAppleSignUp, isPending: isPendingApple } =
    useMutation({
      mutationFn: () => {
        const redirectOrigin = getSocialAuthRedirectOrigin()
        const currentOrigin = window.location.origin
        const payload: AuthorizationSocialAppleStartClientCreateData['body'] = {
          redirect_uri: `${redirectOrigin}${paths.auth.appleClientCallback}`
        }
        if (currentOrigin !== redirectOrigin) {
          payload.return_origin = currentOrigin
        }
        return authorizationSocialAppleStartClientCreate({
          body: payload
        }).then(({ data }) => data)
      },
      onSuccess: (response) => {
        const authUrl = getAuthUrl(response)
        if (!authUrl) throw new Error('Missing Apple auth URL')
        window.location.href = authUrl
      },
      onError: (e) => showApiErrors(e, startForm)
    })

  const { mutateAsync: signupStart, isPending: isPendingStart } = useMutation({
    ...authorizationV2SignupStartCreateMutation(),
    onSuccess: (_data, variables) => {
      setSubmittedEmail(variables.body.email)
    },
    onError: (e) => {
      track('onboarding_step_error', {
        step: CoreStep.EMAIL,
        field: OnboardingErrorField.EMAIL,
        error_code: apiErrorCode(e)
      })
      showApiErrors(e, startForm)
    }
  })

  const signupMethods: AuthMethodButton[] = [
    {
      id: 'google',
      label: t('auth.signUpWithGoogle'),
      shortLabel: 'Google',
      icon: <GoogleBrandIcon />,
      loading: isPendingGoogle,
      disabled: isPendingStart || isPendingApple,
      onClick: () => startGoogleSignUp(),
      testId: 'register-google'
    },
    {
      id: 'apple',
      label: t('auth.signUpWithApple'),
      shortLabel: 'Apple',
      icon: <AppleBrandIcon />,
      loading: isPendingApple,
      disabled: isPendingStart || isPendingGoogle,
      onClick: () => startAppleSignUp(),
      testId: 'register-apple'
    }
  ]

  const onSubmitStart = async () => {
    try {
      const values = await startForm.validateFields()
      const { terms_and_conditions: _terms, ...payload } = values
      await signupStart({ body: { ...payload, language: apiLanguage } })
    } catch {
      // validation errors shown by form
    }
  }

  if (submittedEmail) {
    return (
      <Result
        status="success"
        title={t('registration_email_sent_title')}
        subTitle={t('registration_email_sent_description', {
          email: submittedEmail
        })}
        extra={
          <Flex align="center" justify="center" gap={6}>
            <Text type="secondary">{t('have_account')}</Text>
            <AuthLink to={paths.auth.login}>{t('sign_in')}</AuthLink>
          </Flex>
        }
      />
    )
  }

  return (
    <Flex orientation="vertical" gap={16} className="w-full">
      <AuthHeader
        title={t('welcome_to_skalx')}
        subtitle={t('sign_up_to_create')}
      />

      <Flex orientation="vertical" gap={24}>
        {errorAlert}

        <Form
          form={startForm}
          layout="vertical"
          scrollToFirstError
          requiredMark={false}
          noValidate
          id="register-form"
        >
          <RegisterFormItems />
        </Form>

        <Flex orientation="vertical" gap={16} className={styles.loginActions}>
          <Button
            block
            size="large"
            onClick={onSubmitStart}
            type="primary"
            loading={isPendingStart}
            disabled={isPendingGoogle || isPendingApple || !termsAccepted}
            form="register-form"
            htmlType="submit"
          >
            {t('sign_up')}
          </Button>

          <Flex orientation="vertical" gap={8}>
            <Divider plain className={styles.loginDivider}>
              {t('auth.or')}
            </Divider>

            <SocialMethodButtons methods={signupMethods} />
          </Flex>

          <Flex
            align="center"
            justify="center"
            gap={6}
            className={styles.authFooter}
          >
            <Text type="secondary">{t('have_account')}</Text>
            <AuthLink to={paths.auth.login}>{t('sign_in')}</AuthLink>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  )
}
